import { useState } from 'react';
import { Link } from 'react-router-dom';
import ScrollReveal from '@/components/base/ScrollReveal';

const faqData = [
  {
    id: 'real-gold',
    question: 'Is Joviaa jewellery real gold?',
    answer: 'Joviaa is not solid gold — it is premium gold-look imitation jewellery. Every piece is crafted from 316L surgical-grade stainless steel and coated with genuine 18K gold-tone colour using PVD (Physical Vapour Deposition) technology — the same process used in luxury watches. The result looks, feels, and wears like fine gold jewellery, but at a fraction of the price and with far more durability for everyday life.',
    category: 'Materials & Quality',
  },
  {
    id: 'skin-green',
    question: 'Will it turn my skin green or cause allergies?',
    answer: 'No. Mass-market imitation jewellery often contains brass or copper that oxidises and leaves green marks on skin — Joviaa uses 316L stainless steel which does not oxidise. Additionally, every piece is nickel-free and hypoallergenic. Our nickel release is independently tested to stay below the EU REACH threshold of 0.5 \u03BCg/cm\u00B2/week. If you have extremely sensitive skin or known metal allergies, Joviaa is designed with you in mind.',
    category: 'Materials & Quality',
  },
  {
    id: 'swimming-shower',
    question: 'Can I wear it while swimming or showering?',
    answer: 'Yes to showers and daily washing — no to chlorinated pools and hot tubs. Joviaa is sweat-proof and water-resistant, so showering, hand-washing, and getting caught in the rain are all fine. However, prolonged exposure to chlorinated water (like swimming pools) can gradually affect the PVD plating over time. We recommend removing your jewellery before swimming in chlorinated water, just as you would with fine gold. For salt water, a quick rinse with fresh water afterwards is all you need.',
    category: 'Materials & Quality',
  },
  {
    id: 'order-without-payment',
    question: 'How do I place an order without online payment?',
    answer: 'Simple: browse our collections, add what you love to your cart, and click "Checkout on WhatsApp." There is no payment gateway, no credit card form, and no upfront payment. Your cart contents are sent directly to our team over WhatsApp as a pre-filled message. A real person from Joviaa will confirm your order, answer any questions, and share payment options — all over chat. You only pay once everything is confirmed.',
    category: 'Ordering & Payment',
  },
  {
    id: 'payment-methods',
    question: 'What payment methods are accepted?',
    answer: 'Since all orders are confirmed manually over WhatsApp, we keep payment flexible. Our team can accept UPI (Google Pay, PhonePe, Paytm), bank transfer, and Cash on Delivery (COD) in select cities. The exact payment method will be discussed and agreed upon when our team confirms your order on WhatsApp. We do not store or process any payment information on our website.',
    category: 'Ordering & Payment',
  },
  {
    id: 'exchange-return',
    question: 'What is the exchange and return policy?',
    answer: 'We offer a 7-day exchange policy from the date of delivery. If you receive a piece and it does not meet your expectations, you can exchange it for another product of equal or higher value (you pay the difference). For quality defects or manufacturing issues, we offer a 30-day return window — just share clear photos on WhatsApp and our team will process a replacement or refund. All exchange and return requests are handled manually over WhatsApp to keep things simple and personal.',
    category: 'Ordering & Payment',
  },
  {
    id: 'delivery-time',
    question: 'How long does delivery take?',
    answer: 'Delivery timelines depend on your location and are confirmed on WhatsApp when you place your order. Typically, orders within metro cities (Mumbai, Delhi, Bengaluru, Chennai, Hyderabad, Kolkata) are delivered in 3-5 business days. Tier-2 and Tier-3 cities may take 5-8 business days. Once your order is confirmed on WhatsApp, our team will share an estimated delivery window specific to your pincode.',
    category: 'Ordering & Payment',
  },
  {
    id: 'plating-last',
    question: 'How long does the gold plating last?',
    answer: 'With proper care, Joviaa PVD plating can last years without fading, chipping, or discolouring. Unlike electroplating (used by most mass-market jewellery), PVD bonds the gold-tone colour directly into the steel at a molecular level — it does not sit on top as a separate layer. This makes it dramatically more durable. We have customers who have worn their Joviaa pieces daily for over a year with no visible wear. Following our care instructions (wipe after sweating, avoid chlorinated water, store in the provided pouch) will maximise the life of your plating.',
    category: 'Materials & Quality',
  },
  {
    id: 'gift-packaging',
    question: 'What does the packaging look like? Is it gift-ready?',
    answer: 'Yes. Every Joviaa order arrives in our signature magnetic-close gift box with the Joviaa wordmark in gold foil. Inside, your jewellery is nestled in a soft microsuede insert, accompanied by a thank-you card and a branded anti-tarnish storage pouch. No plastic bags, no flimsy boxes — the unboxing experience is designed to feel premium whether you are treating yourself or gifting someone special. If you would like to add a personalised gift note, just mention it on WhatsApp when your order is confirmed.',
    category: 'Shipping & Packaging',
  },
  {
    id: 'track-order',
    question: 'Can I track my order?',
    answer: 'Since all orders are processed manually, tracking details are shared directly over WhatsApp. Once your order is confirmed and dispatched, our team will send you the courier tracking link and an estimated delivery date. If you ever need an update, simply message us on the same WhatsApp thread — we respond during business hours (Mon-Sat, 10am-7pm IST).',
    category: 'Shipping & Packaging',
  },
  {
    id: 'bulk-gifting',
    question: 'Do you take bulk or corporate gifting orders?',
    answer: 'We do. Joviaa makes for beautiful corporate gifts, wedding favours, and festive gifting — premium packaging, no allergies to worry about, and a price point that works for larger quantities. Bulk orders (10+ pieces) may qualify for custom pricing. Reach out to us on WhatsApp with your requirements (quantity, occasion, timeline) and our team will put together a proposal for you.',
    category: 'Ordering & Payment',
  },
];

const categories = ['Materials & Quality', 'Ordering & Payment', 'Shipping & Packaging'];

export default function FAQPage() {
  const [openId, setOpenId] = useState<string | null>(faqData[0]?.id || null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const toggleAccordion = (id: string) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  const filteredFaqs = activeCategory
    ? faqData.filter((faq) => faq.category === activeCategory)
    : faqData;

  return (
    <main className="min-h-screen bg-background-50">
      {/* Hero */}
      <section className="relative bg-primary-500 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Soft%20warm%20cream%20and%20sand%20abstract%20gradient%20background%20with%20subtle%20golden%20light%20rays%2C%20minimal%20elegant%20texture%2C%20clean%20serene%20aesthetic%2C%20no%20people%2C%20peaceful%20warm%20ambient%20glow&width=1600&height=550&seq=faq-hero-2026&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-center opacity-20"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24 flex flex-col items-center text-center">
          <p className="text-accent-400 text-xs md:text-sm font-label font-semibold uppercase tracking-[0.2em] mb-4">
            Got Questions?
          </p>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 max-w-3xl leading-tight">
            Frequently Asked<br />Questions
          </h1>
          <p className="mt-5 md:mt-6 text-base md:text-lg text-background-50/80 font-label max-w-2xl leading-relaxed">
            Everything you need to know about Joviaa — from materials and care to ordering and delivery. And if you don&apos;t find your answer here, we&apos;re a WhatsApp message away.
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="max-w-3xl mx-auto px-4 md:px-6 py-12 md:py-20">
        {/* Category Filters */}
        <ScrollReveal>
          <div className="flex items-center gap-2 mb-10 overflow-x-auto pb-2 flex-wrap">
            <button
              onClick={() => setActiveCategory(null)}
              className={`px-4 py-2 rounded-full text-xs font-label font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                activeCategory === null
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-50 text-foreground-500 border border-background-300 hover:border-primary-400 hover:text-primary-500'
              }`}
            >
              All Questions
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat === activeCategory ? null : cat)}
                className={`px-4 py-2 rounded-full text-xs font-label font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-50 text-foreground-500 border border-background-300 hover:border-primary-400 hover:text-primary-500'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </ScrollReveal>

        {/* Accordion */}
        <div className="flex flex-col gap-3">
          {filteredFaqs.map((faq, idx) => {
            const isOpen = openId === faq.id;
            return (
              <ScrollReveal key={faq.id} delay={idx * 60}>
                <div className="bg-background-50 rounded-xl border border-background-200 overflow-hidden transition-colors">
                  <button
                    onClick={() => toggleAccordion(faq.id)}
                    className="w-full flex items-center justify-between gap-3 px-5 md:px-6 py-4 md:py-5 text-left cursor-pointer group"
                  >
                    <span className="text-sm md:text-base font-heading font-semibold text-primary-500 group-hover:text-primary-600 transition-colors pr-2">
                      {faq.question}
                    </span>
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                        isOpen
                          ? 'bg-primary-500 text-background-50 rotate-45'
                          : 'bg-background-100 text-foreground-400 group-hover:text-primary-500'
                      }`}
                    >
                      <i className="ri-add-line text-sm"></i>
                    </div>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-out ${
                      isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-5 md:px-6 pb-5 md:pb-6">
                      <p className="text-sm md:text-base text-foreground-600 font-label leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            );
          })}
        </div>

        {/* Empty state for filtered categories */}
        {filteredFaqs.length === 0 && (
          <div className="text-center py-12">
            <p className="text-foreground-500 font-label">No questions found in this category.</p>
          </div>
        )}
      </section>

      {/* Still Have Questions CTA */}
      <ScrollReveal>
        <section className="bg-accent-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-primary-500">
                Still have questions?
              </h2>
              <p className="mt-2 text-sm md:text-base text-foreground-600 font-label max-w-lg">
                We are just a message away. Reach out on WhatsApp and our team will get back to you within a few hours.
              </p>
            </div>
            <div className="flex items-center gap-4 flex-shrink-0 whitespace-nowrap flex-wrap">
              <a
                href="https://wa.me/919686543847?text=Hi%20Joviaa!%20I%20have%20a%20question."
                target="_blank"
                rel="noopener noreferrer"
                className="px-7 py-3 bg-primary-500 text-background-50 rounded-lg font-heading font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-whatsapp-fill"></i>
                </div>
                Chat on WhatsApp
              </a>
              <Link
                to="/shop"
                className="px-7 py-3 border-2 border-primary-500/20 text-primary-500 rounded-lg font-heading font-semibold text-sm hover:border-primary-500/50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Browse Shop
              </Link>
            </div>
          </div>
        </section>
      </ScrollReveal>
    </main>
  );
}